import React from 'react';
import { Card, Typography, List, Row, Col, Input, InputNumber, DatePicker, Form, Button, Alert } from 'antd';
import { EditOutlined, CloseOutlined, PlusOutlined, MinusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Title, Text } = Typography;

const ExtractedDataDisplay = ({
  extractedData,
  editableData,
  isEditing,
  onEditToggle,
  onEditSupplier,
  onEditBillInfo,
  onEditProduct,
  onAddProduct,
  onRemoveProduct,
  onSaveEdits,
  onCancelEdits
}) => {
  return (
    <Card 
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Extracted Data</span>
          <Button
            type={isEditing ? "default" : "primary"}
            icon={isEditing ? <CloseOutlined /> : <EditOutlined />}
            onClick={onEditToggle}
            size="small"
          >
            {isEditing ? 'Cancel Edit' : 'Edit Data'}
          </Button>
        </div>
      }
      style={{ marginTop: 16 }}
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Bill Analysis Complete"
          description={isEditing ? "Edit the extracted data below and save your changes." : "Please review the extracted data below and confirm if it's correct."}
          type="success"
          showIcon
        />
      </div>

      {/* Supplier Information */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>Supplier Information</Title>
        {isEditing ? (
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Name">
                <Input
                  value={editableData?.supplier?.name || ''}
                  onChange={(e) => onEditSupplier('name', e.target.value)}
                  placeholder="Supplier name"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Phone">
                <Input
                  value={editableData?.supplier?.phone || ''}
                  onChange={(e) => onEditSupplier('phone', e.target.value)}
                  placeholder="Phone number"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Address">
                <Input
                  value={editableData?.supplier?.address || ''}
                  onChange={(e) => onEditSupplier('address', e.target.value)}
                  placeholder="Address"
                />
              </Form.Item>
            </Col>
          </Row>
        ) : (
          <div>
            <p><strong>Name:</strong> {extractedData.supplier?.name || 'Not found'}</p>
            <p><strong>Phone:</strong> {extractedData.supplier?.phone || 'Not found'}</p>
            <p><strong>Address:</strong> {extractedData.supplier?.address || 'Not found'}</p>
          </div>
        )}
      </div>

      {/* Bill Information */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>Bill Information</Title>
        {isEditing ? (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Bill Date">
                <DatePicker
                  value={editableData?.billDate ? moment(editableData.billDate) : null}
                  onChange={(date) => onEditBillInfo('billDate', date ? date.format('YYYY-MM-DD') : '')}
                  style={{ width: '100%' }}
                  placeholder="Select bill date"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Bill Number">
                <Input
                  value={editableData?.billNumber || ''}
                  onChange={(e) => onEditBillInfo('billNumber', e.target.value)}
                  placeholder="Bill number"
                />
              </Form.Item>
            </Col>
          </Row>
        ) : (
          <div>
            <p><strong>Date:</strong> {extractedData.billDate || 'Not found'}</p>
            <p><strong>Bill Number:</strong> {extractedData.billNumber || 'Not found'}</p>
          </div>
        )}
      </div>

      {/* Products */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={5}>Products ({extractedData.products?.length || 0})</Title>
          {isEditing && (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={onAddProduct}
              size="small"
            >
              Add Product
            </Button>
          )}
        </div>
        
        {extractedData.products && extractedData.products.length > 0 ? (
          <div>
            {isEditing ? (
              <div>
                {editableData?.products?.map((product, index) => (
                  <Card key={index} size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col span={6}>
                        <Form.Item label="Title">
                          <Input
                            value={product.title || ''}
                            onChange={(e) => onEditProduct(index, 'title', e.target.value)}
                            placeholder="Product title"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item label="ISBN">
                          <Input
                            value={product.isbn || ''}
                            onChange={(e) => onEditProduct(index, 'isbn', e.target.value)}
                            placeholder="ISBN"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item label="Author">
                          <Input
                            value={product.author || ''}
                            onChange={(e) => onEditProduct(index, 'author', e.target.value)}
                            placeholder="Author"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item label="Publisher">
                          <Input
                            value={product.publisher || ''}
                            onChange={(e) => onEditProduct(index, 'publisher', e.target.value)}
                            placeholder="Publisher"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={4}>
                        <Form.Item label="Quantity">
                          <InputNumber
                            value={product.quantity || 1}
                            onChange={(value) => onEditProduct(index, 'quantity', value)}
                            min={1}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item label="Price">
                          <InputNumber
                            value={product.price || 0}
                            onChange={(value) => onEditProduct(index, 'price', value)}
                            min={0}
                            step={0.01}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item label="Currency">
                          <Input
                            value={product.currency || '₹'}
                            onChange={(e) => onEditProduct(index, 'currency', e.target.value)}
                            placeholder="Currency"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item label="Discount %">
                          <InputNumber
                            value={product.discount || 0}
                            onChange={(value) => onEditProduct(index, 'discount', value)}
                            min={0}
                            max={100}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item label="Actions">
                          <Button
                            type="text"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => onRemoveProduct(index)}
                            size="small"
                          >
                            Remove
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            ) : (
              <List
                size="small"
                dataSource={extractedData.products}
                renderItem={(product, index) => (
                  <List.Item>
                    <div>
                      <Text strong>{product.title || `Product ${index + 1}`}</Text>
                      <br />
                      <Text>ISBN: {product.isbn || 'N/A'}</Text> | 
                      <Text> Author: {product.author || 'N/A'}</Text> | 
                      <Text> Publisher: {product.publisher || 'N/A'}</Text>
                      <br />
                      <Text>Qty: {product.quantity || 'N/A'}</Text> | 
                      <Text> Price: {product.price || 'N/A'}</Text> | 
                      <Text> Currency: {product.currency || 'N/A'}</Text> | 
                      <Text> Discount: {product.discount || '0'}%</Text>
                    </div>
                  </List.Item>
                )}
              />
            )}
          </div>
        ) : (
          <Text type="secondary">No products found</Text>
        )}
      </div>

      {/* Edit Mode Action Buttons */}
      {isEditing && (
        <div style={{ textAlign: 'center', marginTop: 24, padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <Button.Group>
            <Button 
              type="primary" 
              icon={<SaveOutlined />}
              onClick={onSaveEdits}
              size="large"
            >
              Save Changes
            </Button>
            <Button 
              icon={<CloseOutlined />}
              onClick={onCancelEdits}
              size="large"
            >
              Cancel
            </Button>
          </Button.Group>
        </div>
      )}
    </Card>
  );
};

export default ExtractedDataDisplay;
